import { Fragment, useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { TopographNavbar } from "../components/TopographNavbar";
import { useAuth } from "../context/AuthContext";
import { CLIENT } from "../lib/api";
import { paymentBadge } from "../lib/status";
import type {
    AdminOrder,
    Coupon,
    OrderStatus,
    PaymentStatus,
    PaymentVerification,
    PostalAddress,
} from "../lib/types";

const dollarFmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const dollars = (cents: number) => dollarFmt.format(cents / 100);
const when = (unixSeconds: number) =>
    unixSeconds ? new Date(unixSeconds * 1000).toLocaleString() : "—";

// Fulfillment states the dropdown can set directly. `refunded` is only
// reachable through the refund flow, mirroring the server's validation.
const FULFILLMENT_CHOICES: OrderStatus[] = [
    "pending",
    "printing",
    "shipped",
    "delivered",
    "stocked",
    "canceled",
];

function PaymentBadge({ status }: { status: PaymentStatus }) {
    const b = paymentBadge(status);
    return <Badge bg={b.variant}>{b.label}</Badge>;
}

function couponEffect(c: Coupon): string {
    if (c.free) return "free order";
    if (c.percentOff) return `${c.percentOff}% off`;
    if (c.creditCents) return `${dollarFmt.format(c.creditCents / 100)} off`;
    return "—";
}

function formatAddress(a: PostalAddress | undefined): string {
    if (!a) return "";
    const parts = [
        ...(a.recipients ?? []),
        ...(a.addressLines ?? []),
        [a.locality, a.administrativeArea, a.postalCode].filter(Boolean).join(", "),
        a.regionCode,
    ].filter((p) => p && String(p).trim() !== "");
    return parts.join(" · ");
}

interface RefundModalState {
    order: AdminOrder;
    amount: string; // dollars, as typed
    reason: string;
    busy: boolean;
    error: string | null;
}

interface CouponModalState {
    editing: boolean; // true = code field locked
    code: string;
    description: string;
    effect: "percent" | "credit" | "free";
    value: string; // percent or dollars, as typed
    maxRedemptions: string;
    expiresAt: string; // datetime-local value, "" = never
    busy: boolean;
    error: string | null;
}

const NEW_COUPON: CouponModalState = {
    editing: false,
    code: "",
    description: "",
    effect: "percent",
    value: "",
    maxRedemptions: "",
    expiresAt: "",
    busy: false,
    error: null,
};

// datetime-local <-> unix seconds, in the browser's zone.
function toLocalInput(unixSeconds: number): string {
    if (!unixSeconds) return "";
    const d = new Date(unixSeconds * 1000);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function Admin() {
    const auth = useAuth();
    const [orders, setOrders] = useState<AdminOrder[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Per-order live verification results, keyed by order id.
    const [verifications, setVerifications] = useState<Record<string, PaymentVerification>>({});
    const [verifying, setVerifying] = useState<Record<string, boolean>>({});
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [tracking, setTracking] = useState<Record<string, string>>({});
    const [refund, setRefund] = useState<RefundModalState | null>(null);
    const [coupons, setCoupons] = useState<Coupon[] | null>(null);
    const [couponModal, setCouponModal] = useState<CouponModalState | null>(null);

    const isAdmin =
        auth.scopes.includes("topograph.admin") || auth.scopes.includes("authz.admin");

    const load = useCallback(async () => {
        try {
            const [ordersResp, couponsResp] = await Promise.all([
                CLIENT.adminListOrders(),
                CLIENT.adminListCoupons(),
            ]);
            setOrders(ordersResp.orders);
            setCoupons(couponsResp.coupons);
            setError(null);
        } catch (err) {
            setError(`Failed to load orders: ${err}`);
        }
    }, []);

    useEffect(() => {
        if (!auth.loggedIn || !isAdmin) return;
        load();
        const id = window.setInterval(load, 15_000);
        return () => window.clearInterval(id);
    }, [auth.loggedIn, isAdmin, load]);

    const applyOrder = (updated: AdminOrder | ((o: AdminOrder) => AdminOrder), id: string) => {
        setOrders((prev) =>
            prev?.map((o) =>
                o.id === id
                    ? typeof updated === "function"
                        ? updated(o)
                        : { ...o, ...updated }
                    : o,
            ) ?? prev,
        );
    };

    const verify = async (o: AdminOrder) => {
        setVerifying((v) => ({ ...v, [o.id]: true }));
        try {
            const v = await CLIENT.adminVerifyPayment(o.id);
            setVerifications((prev) => ({ ...prev, [o.id]: v }));
            setExpanded((prev) => ({ ...prev, [o.id]: true }));
            applyOrder(
                (old) => ({
                    ...old,
                    paymentStatus: v.paymentStatus,
                    amountCents: v.amountCents || old.amountCents,
                    amountRefundedCents: v.amountRefundedCents,
                }),
                o.id,
            );
            setError(null);
        } catch (err) {
            setError(`Payment verification failed: ${err}`);
        } finally {
            setVerifying((v) => ({ ...v, [o.id]: false }));
        }
    };

    const setFulfillment = async (o: AdminOrder, status: OrderStatus, shippingId?: string) => {
        try {
            const updated = await CLIENT.adminSetFulfillment(o.id, { status, shippingId });
            applyOrder((old) => ({ ...old, ...updated }), o.id);
            setError(null);
        } catch (err) {
            setError(`Fulfillment update failed: ${err}`);
        }
    };

    const saveCoupon = async () => {
        if (!couponModal) return;
        const m = couponModal;
        const coupon: Coupon = {
            code: m.code.trim().toLowerCase(),
            description: m.description.trim(),
            redemptions: 0, // server preserves the real tally
            maxRedemptions: m.maxRedemptions ? parseInt(m.maxRedemptions, 10) : 0,
            expiresAt: m.expiresAt ? Math.floor(new Date(m.expiresAt).getTime() / 1000) : 0,
        };
        if (m.effect === "free") {
            coupon.free = true;
        } else {
            const v = parseFloat(m.value);
            if (!Number.isFinite(v) || v <= 0) {
                setCouponModal({ ...m, error: "Enter a positive value." });
                return;
            }
            if (m.effect === "percent") {
                if (v > 100) {
                    setCouponModal({ ...m, error: "Percent must be at most 100." });
                    return;
                }
                coupon.percentOff = v;
            } else {
                coupon.creditCents = Math.round(v * 100);
            }
        }
        setCouponModal({ ...m, busy: true, error: null });
        try {
            await CLIENT.adminPutCoupon(coupon);
            setCouponModal(null);
            await load();
        } catch (err) {
            setCouponModal((cur) => (cur ? { ...cur, busy: false, error: `${err}` } : cur));
        }
    };

    const toggleCoupon = async (c: Coupon) => {
        try {
            await CLIENT.adminPutCoupon({ ...c, disabled: !c.disabled });
            await load();
        } catch (err) {
            setError(`Coupon update failed: ${err}`);
        }
    };

    const deleteCoupon = async (c: Coupon) => {
        try {
            await CLIENT.adminDeleteCoupon(c.code);
            await load();
        } catch (err) {
            setError(`Coupon delete failed: ${err}`);
        }
    };

    const editCoupon = (c: Coupon) => {
        setCouponModal({
            editing: true,
            code: c.code,
            description: c.description ?? "",
            effect: c.free ? "free" : c.percentOff ? "percent" : "credit",
            value: c.free
                ? ""
                : c.percentOff
                  ? String(c.percentOff)
                  : ((c.creditCents ?? 0) / 100).toFixed(2),
            maxRedemptions: c.maxRedemptions ? String(c.maxRedemptions) : "",
            expiresAt: toLocalInput(c.expiresAt ?? 0),
            busy: false,
            error: null,
        });
    };

    const submitRefund = async () => {
        if (!refund) return;
        const dollarsTyped = parseFloat(refund.amount);
        const remaining = refund.order.amountCents - refund.order.amountRefundedCents;
        const cents = Number.isFinite(dollarsTyped) ? Math.round(dollarsTyped * 100) : NaN;
        if (!Number.isFinite(cents) || cents <= 0 || cents > remaining) {
            setRefund({ ...refund, error: `Amount must be between $0.01 and ${dollars(remaining)}.` });
            return;
        }
        setRefund({ ...refund, busy: true, error: null });
        try {
            // Full remaining balance is Stripe's default; send 0 for it so
            // rounding drift can't leave a stray cent behind.
            const v = await CLIENT.adminRefund(refund.order.id, {
                amountCents: cents === remaining ? 0 : cents,
                reason: refund.reason || undefined,
            });
            setVerifications((prev) => ({ ...prev, [refund.order.id]: v }));
            applyOrder(
                (old) => ({
                    ...old,
                    paymentStatus: v.paymentStatus,
                    amountRefundedCents: v.amountRefundedCents,
                    status: v.paymentStatus === "refunded" ? "refunded" : old.status,
                }),
                refund.order.id,
            );
            setRefund(null);
        } catch (err) {
            setRefund((r) => (r ? { ...r, busy: false, error: `${err}` } : r));
        }
    };

    if (auth.loading) {
        return (
            <>
                <TopographNavbar subtitle="admin" />
                <div style={{ margin: "1rem" }}><Spinner size="sm" /> Loading…</div>
            </>
        );
    }

    if (!auth.loggedIn) {
        return (
            <>
                <TopographNavbar subtitle="admin" />
                <div style={{ margin: "1rem" }}>
                    You are not logged in.{" "}
                    <Button variant="outline-warning" size="sm" onClick={() => auth.login("/admin")}>
                        <i className="bi bi-box-arrow-in-right" /> Sign In
                    </Button>
                </div>
            </>
        );
    }

    if (!isAdmin) {
        return (
            <>
                <TopographNavbar subtitle="admin" />
                <div style={{ margin: "1rem", maxWidth: "42rem" }}>
                    <Alert variant="warning">
                        <Alert.Heading as="h6">Admin access required</Alert.Heading>
                        Your current session does not carry the <code>topograph.admin</code>{" "}
                        scope. If your account has the admin role, re-authenticate to elevate
                        this session; otherwise the sign-in will be rejected.
                    </Alert>
                    <Button
                        variant="outline-warning"
                        onClick={() => {
                            window.location.href = `/api/admin/login?next=${encodeURIComponent("/admin")}`;
                        }}
                    >
                        <i className="bi bi-shield-lock" /> Sign in as admin
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            <TopographNavbar subtitle="admin" />
            <div style={{ margin: "1rem" }}>
                <h5>
                    <i className="bi bi-bag-check" /> Orders
                </h5>
                {error && <Alert variant="danger">{error}</Alert>}
                {orders === null ? (
                    <div><Spinner size="sm" /> Loading orders…</div>
                ) : orders.length === 0 ? (
                    <div style={{ color: "#999" }}>No orders yet.</div>
                ) : (
                    <Table hover responsive variant="dark" size="sm" style={{ verticalAlign: "middle" }}>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Created</th>
                                <th>Order</th>
                                <th>Map</th>
                                <th>Customer</th>
                                <th>Total</th>
                                <th>Payment</th>
                                <th>Fulfillment</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => {
                                const v = verifications[o.id];
                                const canRefund =
                                    o.paymentStatus === "succeeded" ||
                                    o.paymentStatus === "partially_refunded";
                                return (
                                    <Fragment key={o.id}>
                                        <tr>
                                            <td>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    style={{ color: "#FFBF00" }}
                                                    onClick={() =>
                                                        setExpanded((prev) => ({ ...prev, [o.id]: !prev[o.id] }))
                                                    }
                                                >
                                                    <i className={`bi bi-chevron-${expanded[o.id] ? "down" : "right"}`} />
                                                </Button>
                                            </td>
                                            <td>{when(o.createTime)}</td>
                                            <td>
                                                <code title={o.id}>{o.id.slice(0, 8)}</code>
                                            </td>
                                            <td title={o.mapId}>
                                                {o.mapFriendlyName || <code>{o.mapId.slice(0, 8)}</code>}
                                            </td>
                                            <td>{o.userEmail}</td>
                                            <td>
                                                {dollars(o.amountCents)}
                                                {(o.discountCents ?? 0) > 0 && (
                                                    <div style={{ color: "#999", fontSize: "0.8em" }}>
                                                        coupon <code>{o.couponCode}</code> −{dollars(o.discountCents!)}
                                                    </div>
                                                )}
                                                {o.amountRefundedCents > 0 && (
                                                    <div style={{ color: "#999", fontSize: "0.8em" }}>
                                                        −{dollars(o.amountRefundedCents)} refunded
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <PaymentBadge status={o.paymentStatus} />{" "}
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    disabled={verifying[o.id] || !o.transactionId}
                                                    title={
                                                        o.transactionId
                                                            ? "Check this payment live against Stripe"
                                                            : "Order has no payment intent"
                                                    }
                                                    onClick={() => verify(o)}
                                                >
                                                    {verifying[o.id] ? (
                                                        <Spinner size="sm" />
                                                    ) : (
                                                        <i className="bi bi-patch-check" />
                                                    )}
                                                    &nbsp;Verify
                                                </Button>
                                            </td>
                                            <td>
                                                <Form.Select
                                                    size="sm"
                                                    value={o.status}
                                                    disabled={o.status === "refunded"}
                                                    onChange={(e) =>
                                                        setFulfillment(o, e.target.value as OrderStatus)
                                                    }
                                                    style={{ minWidth: "8rem" }}
                                                >
                                                    {FULFILLMENT_CHOICES.map((s) => (
                                                        <option key={s} value={s}>
                                                            {s}
                                                        </option>
                                                    ))}
                                                    {!FULFILLMENT_CHOICES.includes(o.status) && (
                                                        <option value={o.status} disabled>
                                                            {o.status}
                                                        </option>
                                                    )}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    disabled={!canRefund}
                                                    title={
                                                        canRefund
                                                            ? "Refund this payment"
                                                            : "Only a successful payment can be refunded"
                                                    }
                                                    onClick={() =>
                                                        setRefund({
                                                            order: o,
                                                            amount: (
                                                                (o.amountCents - o.amountRefundedCents) / 100
                                                            ).toFixed(2),
                                                            reason: "requested_by_customer",
                                                            busy: false,
                                                            error: null,
                                                        })
                                                    }
                                                >
                                                    <i className="bi bi-arrow-counterclockwise" /> Refund
                                                </Button>
                                            </td>
                                        </tr>
                                        {expanded[o.id] && (
                                            <tr>
                                                <td></td>
                                                <td colSpan={8} style={{ fontSize: "0.9em" }}>
                                                    <div style={{ display: "flex", gap: "3rem", flexWrap: "wrap" }}>
                                                        <div>
                                                            <b>Shipping</b>
                                                            <div>{formatAddress(o.shippingAddress) || "—"}</div>
                                                            <div style={{ marginTop: "0.5rem" }}>
                                                                <b>Tracking</b>
                                                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                                                    <Form.Control
                                                                        size="sm"
                                                                        style={{ maxWidth: "14rem" }}
                                                                        placeholder="tracking reference"
                                                                        value={tracking[o.id] ?? o.shippingId}
                                                                        onChange={(e) =>
                                                                            setTracking((prev) => ({
                                                                                ...prev,
                                                                                [o.id]: e.target.value,
                                                                            }))
                                                                        }
                                                                    />
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-secondary"
                                                                        disabled={
                                                                            (tracking[o.id] ?? o.shippingId) === o.shippingId
                                                                        }
                                                                        onClick={() =>
                                                                            setFulfillment(o, o.status, tracking[o.id])
                                                                        }
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <b>Stripe</b>
                                                            <div>
                                                                intent: <code>{o.transactionId || "—"}</code>
                                                            </div>
                                                            {v ? (
                                                                <>
                                                                    <div>
                                                                        status: <code>{v.stripeStatus}</code>{" "}
                                                                        <PaymentBadge status={v.paymentStatus} />
                                                                    </div>
                                                                    <div>
                                                                        received {dollars(v.amountReceivedCents)} / refunded{" "}
                                                                        {dollars(v.amountRefundedCents)}
                                                                    </div>
                                                                    {v.receiptUrl && (
                                                                        <div>
                                                                            <a href={v.receiptUrl} target="_blank" rel="noreferrer">
                                                                                receipt <i className="bi bi-box-arrow-up-right" />
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                    {v.lastError && (
                                                                        <div style={{ color: "#dc3545" }}>
                                                                            last error: {v.lastError}
                                                                        </div>
                                                                    )}
                                                                    {(v.refunds?.length ?? 0) > 0 && (
                                                                        <div>
                                                                            <b>Refunds</b>
                                                                            {v.refunds!.map((r) => (
                                                                                <div key={r.id}>
                                                                                    <code>{r.id.slice(0, 11)}</code>{" "}
                                                                                    {dollars(r.amountCents)} — {r.status}
                                                                                    {r.reason ? ` (${r.reason})` : ""},{" "}
                                                                                    {when(r.createTime)}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    <div style={{ color: "#999" }}>
                                                                        checked {when(v.checkedAt)}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div style={{ color: "#999" }}>
                                                                    Press Verify to fetch the live payment state.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                )}

                <h5 style={{ marginTop: "2rem" }}>
                    <i className="bi bi-ticket-perforated" /> Coupons{" "}
                    <Button
                        size="sm"
                        variant="outline-warning"
                        style={{ marginLeft: "0.5rem" }}
                        onClick={() => setCouponModal({ ...NEW_COUPON })}
                    >
                        <i className="bi bi-plus-lg" /> New coupon
                    </Button>
                </h5>
                {coupons === null ? (
                    <div><Spinner size="sm" /> Loading coupons…</div>
                ) : coupons.length === 0 ? (
                    <div style={{ color: "#999" }}>No coupons defined.</div>
                ) : (
                    <Table hover responsive variant="dark" size="sm" style={{ verticalAlign: "middle" }}>
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Effect</th>
                                <th>Description</th>
                                <th>Uses</th>
                                <th>Expires</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {coupons.map((c) => {
                                const expired = !!c.expiresAt && c.expiresAt * 1000 <= Date.now();
                                const exhausted =
                                    !!c.maxRedemptions && c.redemptions >= c.maxRedemptions;
                                return (
                                    <tr key={c.code}>
                                        <td><code>{c.code}</code></td>
                                        <td>{couponEffect(c)}</td>
                                        <td>{c.description}</td>
                                        <td>
                                            {c.redemptions}
                                            {c.maxRedemptions ? ` / ${c.maxRedemptions}` : " / ∞"}
                                        </td>
                                        <td>{c.expiresAt ? when(c.expiresAt) : "never"}</td>
                                        <td>
                                            {c.disabled ? (
                                                <Badge bg="secondary">disabled</Badge>
                                            ) : expired ? (
                                                <Badge bg="secondary">expired</Badge>
                                            ) : exhausted ? (
                                                <Badge bg="warning">fully redeemed</Badge>
                                            ) : (
                                                <Badge bg="success">active</Badge>
                                            )}
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                title="Edit"
                                                onClick={() => editCoupon(c)}
                                            >
                                                <i className="bi bi-pencil" />
                                            </Button>{" "}
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                title={c.disabled ? "Enable" : "Disable"}
                                                onClick={() => toggleCoupon(c)}
                                            >
                                                <i className={`bi bi-${c.disabled ? "play" : "pause"}`} />
                                            </Button>{" "}
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                title="Delete"
                                                onClick={() => deleteCoupon(c)}
                                            >
                                                <i className="bi bi-trash" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </div>

            <Modal
                show={refund !== null}
                onHide={() => refund && !refund.busy && setRefund(null)}
                data-bs-theme="dark"
            >
                {refund && (
                    <>
                        <Modal.Header closeButton={!refund.busy}>
                            <Modal.Title as="h6">
                                Refund order <code>{refund.order.id.slice(0, 8)}</code>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div style={{ marginBottom: "0.75rem" }}>
                                {refund.order.userEmail} — paid {dollars(refund.order.amountCents)}
                                {refund.order.amountRefundedCents > 0 &&
                                    `, already refunded ${dollars(refund.order.amountRefundedCents)}`}
                                .
                            </div>
                            <Form.Group style={{ marginBottom: "0.75rem" }}>
                                <Form.Label>Amount (USD)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={refund.amount}
                                    disabled={refund.busy}
                                    onChange={(e) => setRefund({ ...refund, amount: e.target.value })}
                                />
                                <Form.Text>
                                    Up to{" "}
                                    {dollars(refund.order.amountCents - refund.order.amountRefundedCents)}{" "}
                                    remaining.
                                </Form.Text>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Reason</Form.Label>
                                <Form.Select
                                    value={refund.reason}
                                    disabled={refund.busy}
                                    onChange={(e) => setRefund({ ...refund, reason: e.target.value })}
                                >
                                    <option value="requested_by_customer">requested by customer</option>
                                    <option value="duplicate">duplicate</option>
                                    <option value="fraudulent">fraudulent</option>
                                </Form.Select>
                            </Form.Group>
                            {refund.error && (
                                <Alert variant="danger" style={{ marginTop: "0.75rem", marginBottom: 0 }}>
                                    {refund.error}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="outline-secondary"
                                disabled={refund.busy}
                                onClick={() => setRefund(null)}
                            >
                                Cancel
                            </Button>
                            <Button variant="danger" disabled={refund.busy} onClick={submitRefund}>
                                {refund.busy ? <Spinner size="sm" /> : <i className="bi bi-arrow-counterclockwise" />}
                                &nbsp;Refund {refund.amount ? `$${refund.amount}` : ""}
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>

            <Modal
                show={couponModal !== null}
                onHide={() => couponModal && !couponModal.busy && setCouponModal(null)}
                data-bs-theme="dark"
            >
                {couponModal && (
                    <>
                        <Modal.Header closeButton={!couponModal.busy}>
                            <Modal.Title as="h6">
                                {couponModal.editing ? (
                                    <>Edit coupon <code>{couponModal.code}</code></>
                                ) : (
                                    "New coupon"
                                )}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {!couponModal.editing && (
                                <Form.Group className="mb-2">
                                    <Form.Label>Code</Form.Label>
                                    <Form.Control
                                        placeholder="e.g. launch-20"
                                        value={couponModal.code}
                                        disabled={couponModal.busy}
                                        onChange={(e) =>
                                            setCouponModal({ ...couponModal, code: e.target.value })
                                        }
                                    />
                                    <Form.Text>
                                        Lowercase letters, digits, - and _. Customers type this at checkout.
                                    </Form.Text>
                                </Form.Group>
                            )}
                            <Form.Group className="mb-2">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    placeholder="internal note / shown to customer on apply"
                                    value={couponModal.description}
                                    disabled={couponModal.busy}
                                    onChange={(e) =>
                                        setCouponModal({ ...couponModal, description: e.target.value })
                                    }
                                />
                            </Form.Group>
                            <div style={{ display: "flex", gap: "0.5rem" }} className="mb-2">
                                <Form.Group style={{ flex: 1 }}>
                                    <Form.Label>Effect</Form.Label>
                                    <Form.Select
                                        value={couponModal.effect}
                                        disabled={couponModal.busy}
                                        onChange={(e) =>
                                            setCouponModal({
                                                ...couponModal,
                                                effect: e.target.value as CouponModalState["effect"],
                                            })
                                        }
                                    >
                                        <option value="percent">percent off</option>
                                        <option value="credit">fixed credit</option>
                                        <option value="free">free order</option>
                                    </Form.Select>
                                </Form.Group>
                                {couponModal.effect !== "free" && (
                                    <Form.Group style={{ flex: 1 }}>
                                        <Form.Label>
                                            {couponModal.effect === "percent" ? "Percent (1-100)" : "Credit (USD)"}
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0.01"
                                            step={couponModal.effect === "percent" ? "1" : "0.01"}
                                            value={couponModal.value}
                                            disabled={couponModal.busy}
                                            onChange={(e) =>
                                                setCouponModal({ ...couponModal, value: e.target.value })
                                            }
                                        />
                                    </Form.Group>
                                )}
                            </div>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                <Form.Group style={{ flex: 1 }}>
                                    <Form.Label>Max uses</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="unlimited"
                                        value={couponModal.maxRedemptions}
                                        disabled={couponModal.busy}
                                        onChange={(e) =>
                                            setCouponModal({ ...couponModal, maxRedemptions: e.target.value })
                                        }
                                    />
                                </Form.Group>
                                <Form.Group style={{ flex: 2 }}>
                                    <Form.Label>Expires</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={couponModal.expiresAt}
                                        disabled={couponModal.busy}
                                        onChange={(e) =>
                                            setCouponModal({ ...couponModal, expiresAt: e.target.value })
                                        }
                                    />
                                </Form.Group>
                            </div>
                            {couponModal.error && (
                                <Alert variant="danger" style={{ marginTop: "0.75rem", marginBottom: 0 }}>
                                    {couponModal.error}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="outline-secondary"
                                disabled={couponModal.busy}
                                onClick={() => setCouponModal(null)}
                            >
                                Cancel
                            </Button>
                            <Button variant="warning" disabled={couponModal.busy} onClick={saveCoupon}>
                                {couponModal.busy ? <Spinner size="sm" /> : <i className="bi bi-check-lg" />}
                                &nbsp;Save
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </>
    );
}
